import { useState } from "react"

import { ESTADOS, type EstadoTarea, type Tarea, TarjetaTarea } from "./TarjetaTarea"

/**
 * El tablero por columnas, una por estado.
 *
 * ⚠ **Quien decide qué escribe un `drop` es la pantalla, no este componente.** Aquí
 * sólo se transporta el identificador de la tarjeta y la clave de la columna donde
 * cayó; qué significa eso lo resuelve quien lo monta.
 *
 * ⚠ **Lo que se arrastra es el identificador, no el índice.** Una tarjeta filtrada
 * fuera de la vista cambia de posición, y el índice movería la equivocada.
 */

interface Columna {
  clave: string
  rotulo: string
  tareas: Tarea[]
}

type Props = {
  tareas: Tarea[]
  puedeMover: boolean
  puedeSoltar: boolean
  nombreDe: (id: number | null) => string | undefined
  onAbrir: (id: number) => void
  onMover: (id: number, estado: EstadoTarea) => void
  onSoltar: (clave: string, id: number) => void
  /** La que tiene la ficha abierta, para que se vea de cual habla. */
  seleccionada?: number | null
  /** Resuelve el codigo de una macro contra lo que ya esta cargado. */
  codigoDe: (id: number) => string | undefined
}

export function TableroTareas({
  tareas,
  puedeMover,
  puedeSoltar,
  nombreDe,
  onAbrir,
  onMover,
  onSoltar,
  seleccionada = null,
  codigoDe,
}: Props) {
  // Sobre qué columna está el puntero. Sólo pinta: lo que escribe es el `drop`.
  const [columnaActiva, setColumnaActiva] = useState<string | null>(null)

  const columnas: Columna[] = ESTADOS.map((estado) => ({
    clave: estado,
    rotulo: estado.replaceAll("_", " "),
    tareas: tareas.filter((t) => t.estado === estado),
  }))

  return (
    <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
      {columnas.map((columna) => (
        <section
          key={columna.clave}
          aria-label={columna.rotulo}
          data-testid={`columna-${columna.clave}`}
          // `preventDefault` en `dragOver` es lo que declara la zona como soltable:
          // sin él el navegador rechaza el `drop` y el arrastre parece funcionar
          // hasta que se suelta.
          onDragOver={(e) => {
            if (!puedeSoltar) return
            e.preventDefault()
            setColumnaActiva(columna.clave)
          }}
          onDragLeave={() => setColumnaActiva(null)}
          onDrop={(e) => {
            e.preventDefault()
            setColumnaActiva(null)
            onSoltar(columna.clave, Number(e.dataTransfer.getData("text/plain")))
          }}
          // ⚠ **La columna tiene fondo y borde propios, y no es decoración: es la
          // zona de soltado.** Antes sólo declaraba el radio —que no recortaba
          // nada— y un contorno punteado que aparecía *durante* el arrastre. Una
          // columna vacía era literalmente invisible: no había dónde ver que se
          // podía soltar, y el radio se aplicaba sobre la nada.
          className="flex flex-col superficie p-2"
          style={{
            borderColor:
              columnaActiva === columna.clave ? "var(--primary-texto)" : "var(--cristal-filo)",
            outline:
              columnaActiva === columna.clave ? "2px dashed var(--primary-texto)" : undefined,
            outlineOffset: "4px",
          }}
        >
          <h2 className="mb-2 flex items-center justify-between gap-2 px-1 text-xs font-medium uppercase tracking-wide">
            <span className="truncate">{columna.rotulo}</span>
            <span className="tabular" style={{ color: "var(--fg-muted)" }}>
              {columna.tareas.length}
            </span>
          </h2>
          {/* Alto mínimo para que una columna sin tarjetas siga siendo un blanco al
              que apuntar. Sin esto se encoge hasta su rótulo y hay que soltar sobre
              una franja de dos centímetros. */}
          <div className="min-h-24 flex-1 space-y-2">
            {columna.tareas.map((tarea) => (
              <TarjetaTarea
                key={tarea.id}
                tarea={tarea}
                puedeMover={puedeMover}
                nombreAsignado={nombreDe(tarea.asignado_a)}
                onAbrir={() => onAbrir(tarea.id)}
                seleccionada={tarea.id === seleccionada}
                codigoPadre={
                  tarea.tarea_padre_id === null ? undefined : codigoDe(tarea.tarea_padre_id)
                }
                onArrastrar={puedeSoltar ? () => setColumnaActiva(null) : undefined}
                onMover={(nuevo) => onMover(tarea.id, nuevo)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
